import { useContext } from 'react';

import type { AxiosError } from 'axios';
import axios from 'axios';
import config from 'config/config';
import { useCall } from 'context/call-context';
import { CustomerContext } from 'context/customer-context';
import { useStoreDetails } from 'context/store-context';
import { browserName, isBrowser } from 'react-device-detect';

import type { Action, ActivityDto, ProductDto } from './models';
import { Actor, Device, Index } from './models';

declare const VERSION: string;

type logerData = {
  label?: string;
  description?: string;
  agentUsername?: string;
  sessionID?: string | undefined;
  action: Action;
  index?: Index;
  products?: ProductDto[];
  data?: Object;
  startTime?: Date;
  survey?: unknown;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
const useActivityLogger = (): { log: (activity: logerData) => Promise<void> } => {
  const { customer, fingerprint, id } = useContext(CustomerContext);
  const { name, baseUrl, widgetOption } = useStoreDetails();
  const { team, product, productType } = useCall();
  const customerId: string | number | null = localStorage.getItem('customer')
    ? localStorage.getItem('customer')
    : Math.random();
  const log = async ({
    label,
    description,
    agentUsername,
    sessionID,
    action,
    index,
    products,
    data,
    survey,
    startTime
  }: logerData): Promise<void> => {
    try {
      const activityData: ActivityDto = {
        action,
        actor: Actor.CUSTOMER,
        appVersion: {
          customer: VERSION
        },
        call: {
          agentName: agentUsername ?? '',
          device: isBrowser ? Device.BROWSER : Device.MOBILE,
          endTime: startTime && new Date(),
          sessionId: sessionID ?? 'NO_SESSION',
          startTime,
          team
        },
        customer: {
          countryName: customer.country_name,
          fingerprint,
          id,
          ip: customer.ip,
          timeZone: customer.time_zone?.name
        },
        data: { ...data, customerId },
        description,
        device: {
          browserName,
          fingerprint,
          ip: customer?.ip,
          type: isBrowser ? Device.BROWSER : Device.MOBILE
        },
        index: index ?? Index.ACTIVITY,
        label,
        originUrl: window.location.href,
        product: {
          id: product?.id,
          name: product?.title,
          price: product?.price,
          productType: productType || product?.productType
        },
        products,
        store: {
          baseUrl: baseUrl,
          name: name
        },
        survey,
        timestamp: new Date(),
        widget: {
          position: isBrowser
            ? widgetOption.widgetPosition?.desktop
            : widgetOption.widgetPosition?.mobile ?? widgetOption?.position,
          type: isBrowser ? widgetOption.widgetType?.desktop : widgetOption.widgetType?.mobile ?? widgetOption.type
        }
      };
      if (agentUsername) {
        activityData.user = { role: 'agent', username: agentUsername };
      }
      if (config.logger.enabled) {
        await axios.post(`${config.logger.url}/logger/api/activity`, activityData);
      }
    } catch (e) {
      console.error(`failed to send logs error:${(e as AxiosError).message}`);
    }
  };

  return {
    log
  };
};

export default useActivityLogger;
